import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Table,
  Chip,
  IconButton,
  Input,
  Select,
  Option,
  FormControl,
  FormLabel,
  Stack,
  Badge,
  Tooltip,
} from '@mui/joy';
import {
  MagnifyingGlass as SearchIcon,
  LinkSimple as LinkIcon,
  ArrowsClockwise as RefreshIcon,
  Funnel as FilterListIcon,
  Info as InfoOutlinedIcon,
  PencilSimple as EditIcon,
} from '@phosphor-icons/react';
import { insuranceApi, type InsurancePayoutItem, type InsuranceProvider } from '../../lib/insuranceApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  onMapItem?: (item: InsurancePayoutItem) => void;
  onEditItem?: (item: InsurancePayoutItem) => void;
  refreshTrigger?: number;
}

export default function InsuranceItemManager({ onMapItem, onEditItem, refreshTrigger }: Props) {
  const { showError } = useNotification();
  const [items, setItems] = useState<InsurancePayoutItem[]>([]);
  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    loadProviders();
    loadItems(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadItems(1);
  }, [selectedProvider, selectedCategory, showUnmappedOnly]);

  // Reload items when refreshTrigger changes (e.g., after adding mappings)
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadItems(currentPage);
    }
  }, [refreshTrigger]);

  useEffect(() => {
    loadItems(currentPage);
  }, [currentPage]);

  const loadProviders = async () => {
    try {
      const response = await insuranceApi.providers.list({ is_active: true });
      setProviders(response?.results || []);
    } catch (err: any) {
      showError('Failed to load insurance providers');
    }
  };

  const loadItems = async (page: number) => {
    setLoading(true);
    try {
      const params: any = {
        is_active: true,
        page,
      };

      if (selectedProvider) params.provider = selectedProvider;
      if (selectedCategory) params.category = selectedCategory;
      if (showUnmappedOnly) params.unmapped = true;
      if (searchQuery) params.search = searchQuery;

      const response = await insuranceApi.payoutItems.list(params);
      setItems(response?.results || []);
      setTotalCount(response?.count || 0);
    } catch (err: any) {
      showError('Failed to load insurance items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems(1);
  };

  const getCategoryColor = (category: string): any => {
    const colors: Record<string, any> = {
      RFG: 'primary',
      PNT: 'warning',
      MISC: 'neutral',
    };
    return colors[category] || 'neutral';
  };

  const formatPrice = (price: any): string => {
    if (!price) return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const getPrimaryPrice = (item: InsurancePayoutItem): string => {
    const price = item.replace_price || item.remove_price || item.detach_reset_price;
    return formatPrice(price);
  };

  return (
    <Card>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography level="h3">
            Insurance Payout Items
            <Typography level="body-sm" sx={{ ml: 2, display: 'inline' }}>
              ({totalCount} items)
            </Typography>
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<RefreshIcon />}
            onClick={() => loadItems(currentPage)}
            loading={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <CollapsibleFilters><Stack spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Search</FormLabel>
              <Input
                placeholder="Search by selector code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                endDecorator={
                  <IconButton onClick={handleSearch} size="sm">
                    <SearchIcon />
                  </IconButton>
                }
              />
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <FormLabel>Provider</FormLabel>
              <Select
                value={selectedProvider}
                onChange={(_, value) => setSelectedProvider(value as number | null)}
                placeholder="All providers"
              >
                <Option value={null}>All providers</Option>
                {providers.map((provider) => (
                  <Option key={provider.id} value={provider.id}>
                    {provider.name} {provider.version && `(v${provider.version})`}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <FormLabel>Category</FormLabel>
              <Select
                value={selectedCategory}
                onChange={(_, value) => setSelectedCategory(value as string)}
                placeholder="All"
              >
                <Option value="">All</Option>
                <Option value="RFG">Roofing (RFG)</Option>
                <Option value="PNT">Painting (PNT)</Option>
                <Option value="MISC">Misc</Option>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={showUnmappedOnly ? 'solid' : 'outlined'}
              size="sm"
              startDecorator={<FilterListIcon />}
              onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
            >
              {showUnmappedOnly ? 'Showing Unmapped Only' : 'Show Unmapped Only'}
            </Button>
          </Box>
        </Stack></CollapsibleFilters>

        {/* Items Table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="sm">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Category</th>
                <th style={{ width: 100 }}>Selector</th>
                <th>Description</th>
                <th style={{ width: 60 }}>Unit</th>
                <th style={{ width: 90 }}>Replace</th>
                <th style={{ width: 90 }}>Remove</th>
                <th style={{ width: 90 }}>D&R</th>
                <th style={{ width: 50 }}>Info</th>
                <th style={{ width: 100 }}>Mappings</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Chip size="sm" color={getCategoryColor(item.category)}>
                      {item.category}
                    </Chip>
                  </td>
                  <td>
                    <Typography level="body-sm" fontWeight="md">
                      {item.selector_code}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {item.description}
                    </Typography>
                  </td>
                  <td>{item.unit_of_measure || '-'}</td>
                  <td>
                    <Typography level="body-sm">
                      {formatPrice(item.replace_price)}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {formatPrice(item.remove_price)}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {formatPrice(item.detach_reset_price)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {item.supporting_info ? (
                      <Tooltip
                        title={
                          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                            {item.supporting_info}
                          </Typography>
                        }
                        placement="left"
                        variant="outlined"
                      >
                        <InfoOutlinedIcon
                          size={18}
                          color="var(--joy-palette-primary-500)"
                          style={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {item.mapped_distributors_count ? (
                      <Badge badgeContent={item.mapped_distributors_count} color="success">
                        <Chip size="sm" color="success" variant="soft">
                          Mapped
                        </Chip>
                      </Badge>
                    ) : (
                      <Chip size="sm" color="neutral" variant="soft">
                        Unmapped
                      </Chip>
                    )}
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        onClick={() => onEditItem && onEditItem(item)}
                        title="Edit Item"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        onClick={() => onMapItem && onMapItem(item)}
                        title="Map to Distributors"
                      >
                        <LinkIcon />
                      </IconButton>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {items.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
                {searchQuery || selectedProvider || selectedCategory || showUnmappedOnly
                  ? 'No items found matching your filters'
                  : 'No insurance payout items available'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="body-sm" color="neutral">
            {totalCount > 0
              ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} items`
              : 'No items'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
